import { EntityInfoPanel } from '@aibees/elephant/components/entity-info-panel';
import { IndustryTags } from '@aibees/elephant/components/industry-tags';
import { useCompanyReadDataContext } from '@aibees/elephant/context';
import { Icon, TextEllipsis } from '@aibees/shared/components';
import { HighlightableContent } from '@aibees/shared/features';
import { capitalize, classnames } from '@aibees/shared/utils';
import { Divider, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo } from 'react';
import { NaicsTags } from '../naics-tags';
import * as SX from './sx';
import type { IndustryInfoProps as Props } from './types';

export const IndustryInfo = memo(({ className, hideHeader, config }: Props) => {
  const theme = useTheme();
  const { api } = useCompanyReadDataContext();

  return (
    <EntityInfoPanel
      className={classnames('IndustryInfo', className)}
      sx={SX.root(hideHeader)}
      icon={'factory'}
      title={!hideHeader && 'Industry'}
    >
      <IndustryTags
        items={
          api?.data?.industries?.map((i) => ({ label: capitalize(i) })) ?? null
        }
        config={config}
      />
      {api?.data?.naics && (
        <>
          <Divider />
          <NaicsTags />
        </>
      )}

      {api?.data?.summary?.industry && (
        <>
          <Divider />
          <Stack sx={SX.row}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon
                name="engineering-outline"
                color={theme.palette.grey[500]}
                size={19}
              />

              <HighlightableContent
                content="Primary Industry"
                variant="body4"
                fontWeight="light"
                color="grey.700"
              />
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TextEllipsis variant="body4" fontWeight={700} color="grey.700">
                {capitalize(api?.data.summary.industry)}
              </TextEllipsis>
            </Stack>
          </Stack>
        </>
      )}

      {api?.data?.sic && (
        <>
          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            gap={1}
            padding={1.5}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon
                name="cell-tower"
                color={theme.palette.grey[500]}
                size={19}
              />

              <HighlightableContent
                content="SIC Code"
                variant="body4"
                fontWeight="light"
                color="grey.700"
              />
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <HighlightableContent
                content={api?.data?.sic?.join(', ') ?? 'N/A'}
                variant="body4"
                fontWeight="700"
                color="grey.700"
              />
            </Stack>
          </Stack>
        </>
      )}
    </EntityInfoPanel>
  );
});

IndustryInfo.displayName = 'IndustryInfo';
